import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import ActionBar from "../../../Components/Button/ActionBar";
import NewsMediaForm from "../../../Components/Forms/NewsMediaForm";
import Delete from "../../../Components/Button/Delete";
import axiosRetry from "axios-retry";

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: any) => {
    return error.response?.status === 429;
  },
});

export default function NewsMedia() {
  const location = useLocation();
  const id = location.state && location.state.id;
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const isMounted = useRef(false);

  const [year, setYear] = useState("");
  const [artist, setArtist] = useState("");
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [media, setMedia] = useState("");
  const [datePosted, setDatePosted] = useState("");
  const [link, setLink] = useState("");
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnail_name, setThumbnailName] = useState<string>("");

  // Handle form input changes
  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setYear(e.target.value);
  const handleArtistChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setArtist(e.target.value);
  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCategory(e.target.value);
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTitle(e.target.value);
  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMedia(e.target.value);
  const handleDatePostedChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDatePosted(e.target.value);
  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLink(e.target.value);

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        // 2MB limit
        alert("Thumbnail image size should not exceed 2MB");
        return;
      }
      setThumbnail(file);
    }
  };

  // Common function to build FormData
  const buildFormData = (): FormData | null => {
    console.log("Validation Check:", {
      Year: year,
      Artist: artist,
      Category: category,
      Title: title,
      Media: media,
      DatePosted: datePosted,
      Link: link,
      Thumbnail: thumbnail,
    });

    if (
      !year ||
      !artist ||
      !category ||
      !title ||
      !media ||
      !datePosted ||
      !link
    ) {
      alert("Please fill in all required fields.");
      return null;
    }

    // Require thumbnail only when creating (no id)
    if (!id && !thumbnail) {
      alert("Please upload a thumbnail image.");
      return null;
    }

    // Validate Link format (basic)
    try {
      new URL(link);
    } catch (_) {
      alert("Please enter a valid URL for the Link field.");
      return null;
    }

    const formData = new FormData();
    formData.append("Year", year); // Match database column name
    formData.append("Artist", artist); // Match database column name
    formData.append("Category", category); // Match database column name
    formData.append("Title", title); // Match database column name
    formData.append("Media", media); // Match database column name
    formData.append("DatePosted", datePosted); // Match database column name
    formData.append("Link", link); // Match database column name

    if (thumbnail) {
      formData.append("Thumbnail", thumbnail, thumbnail.name); // Match database column name
    }

    if (id) {
      formData.append("_method", "PUT");
    }

    return formData;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const formData = buildFormData();
    if (!formData) {
      setIsSubmitting(false);
      return;
    }

    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.post(
        "https://cdn.pulp.ph/api/v1/pulpliveworld/news-media",
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("Form submitted successfully!");
      navigate("/view/post/news-Media");
    } catch (error) {
      console.error("Submission Error:", error);
      alert("There was an error submitting the form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle save as draft
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);

    const formData = buildFormData();
    if (!formData) {
      setIsSaving(false);
      return;
    }
    // ...existing code...
  };

  // Handle deletion
  const handleDelete = async () => {
    if (!id) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this content?"
    );
    if (!confirmDelete) return;

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        alert("Authentication token not found!");
        return;
      }

      await axios.delete(
        `https://cdn.pulp.ph/api/v1/pulpliveworld/news-media/${id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      alert("Content Deleted Successfully!");
      navigate("/view/post/newsMedia");
    } catch (error) {
      console.error("Error deleting content:", error);
      alert("There was an error deleting the content. Please try again.");
    }
  };

  // Fetch existing data if editing
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const authToken = localStorage.getItem("authToken");
        const response = await axios.get(
          `https://cdn.pulp.ph/api/v1/pulpliveworld/news-media/${id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              Accept: "application/json",
            },
          }
        );

        const data = response.data.data || response.data;
        setYear(data.year || "");
        setArtist(data.artist || "");
        setCategory(data.category || "");
        setTitle(data.title || "");
        setMedia(data.media || "");
        setDatePosted(data.date_posted ? data.date_posted.split("T")[0] : "");
        setLink(data.link || "");
        setThumbnailName(data.thumbnail_name || "");
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [id]);

  // Set role on mount
  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    setRole(storedRole);
  }, []);

  return (
    <div className="w-full">
      <div className="Dashboard-container flex place-content-between">
        <h1 className="mt-5 font-semibold">News & Media</h1>
        <ActionBar
          role={role}
          onSubmit={handleSubmit}
          onSave={handleSave}
          isSubmitting={isSubmitting}
          isSaved={isSaving}
        />
      </div>
      <div className="container mt-5 pt-5 px-4 rounded bg-white">
        <NewsMediaForm
          year={year}
          artist={artist}
          category={category}
          title={title}
          media={media}
          datePosted={datePosted}
          link={link}
          thumbnail={thumbnail}
          thumbnail_name={thumbnail_name}
          onYearChange={handleYearChange}
          onArtistChange={handleArtistChange}
          onCategoryChange={handleCategoryChange}
          onTitleChange={handleTitleChange}
          onMediaChange={handleMediaChange}
          onDatePostedChange={handleDatePostedChange}
          onLinkChange={handleLinkChange}
          onThumbnailChange={handleThumbnailChange}
        />
        <div className="w-full border-1 border-black rounded mb-5"></div>
        <Delete onDelete={handleDelete} />
      </div>
    </div>
  );
}
